import { useMemo } from 'react';

import { useRecoilValue } from 'recoil';
import { useFormikContext } from 'formik';

import useModal from 'lib/hooks/useModal';
import { useUpdatePracticeBranding } from 'lib/hooks/queries/practice/useUpdatePracticeBranding';

import Button from 'components/shared/Button/Button';
import { currentPractice } from 'components/practice/practiceState';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import SectionList from 'components/practice/UserPreferences/components/SectionList';

import BrandSwatch from './components/BrandSwatch';
import BrandColorsModal from './components/BrandColorsModal';
import {
  getBrandingSummary,
  isTwoColor,
  normalizeBranding,
  resolveBranding,
  withBranding
} from './lib';

const DESCRIPTION =
  "These colors style the screens your patients see - self-scheduling, check-in, instant packets, and the kiosk - and the emails they receive, such as appointment reminders, receipts, and invoices. Your practice logo appears in those emails too. Your practice portal keeps its Myriad look and won't change.";

const PracticeBranding = () => {
  const { values, dirty } = useFormikContext();
  const practice = useRecoilValue(currentPractice);
  const { isOpen, openModal, closeModal } = useModal();

  const savedBranding = values?.displaySettings?.branding;
  const draft = useMemo(() => normalizeBranding(savedBranding), [savedBranding]);
  const resolved = resolveBranding(draft);
  const summary = getBrandingSummary(draft);

  // Branding saves on its own from the modal, and the practice refetch that follows resets the
  // whole page form. Customize stays disabled while the form is dirty so no other edit is lost.
  const { mutate: saveBranding, isLoading: isSaving } = useUpdatePracticeBranding({
    onSuccess: closeModal
  });

  const handleSave = (nextDraft) =>
    saveBranding({
      practiceId: practice?.id,
      displaySettings: withBranding(practice?.display_settings, nextDraft),
      nextDraft
    });

  return (
    <SectionList title="Brand colors" description={DESCRIPTION}>
      <div className="flex items-center gap-4 rounded-xl border border-solid border-neutral-300 bg-white py-3 pl-4 pr-5 shadow-[0px_4px_12px_0px_rgba(65,70,81,0.08)]">
        <BrandSwatch
          primary={resolved.primary_color}
          accent={resolved.secondary_color}
          isTwoColor={isTwoColor(draft)}
          size={64}
          className="shrink-0"
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="text-lg font-500 leading-7 text-neutral-900">{summary.title}</p>
          <p className="text-sm leading-[22px] text-neutral-700">{summary.description}</p>
        </div>
        <Button
          text="Customize"
          icon="brush-mono"
          variant="tertiary"
          color="neutral"
          onClick={openModal}
          isDisabled={dirty}
          tooltip={dirty ? 'Save or discard your other changes first' : null}
          data-qa="customize-brand-colors"
        />
      </div>
      <BrandColorsModal
        isOpen={isOpen}
        handleClose={closeModal}
        branding={savedBranding}
        practice={practice}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </SectionList>
  );
};

export default withErrorBoundary(PracticeBranding);
